import React, { useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  z-index: 10000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  cursor: pointer;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  line-height: 1;
  opacity: 0.8;
  &:hover {
    opacity: 1;
  }
`;

const Image = styled.img`
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
  border-radius: 8px;
  cursor: default;
`;

const DishName = styled.div`
  color: white;
  font-size: 1.1rem;
  font-weight: 600;
  margin-top: 0.75rem;
  text-align: center;
`;

/**
 * Full-screen lightbox modal for viewing dish/hero images.
 *
 * @param {string} imageUrl - Full-size image URL
 * @param {string} [name] - Optional dish name to display below image
 * @param {function} onClose - Called when modal should close
 */
const ImageLightbox = ({ imageUrl, name, onClose }) => {
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") onClose();
    },
    [onClose]
  );

  useEffect(
    function registerEscapeKey() {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    },
    [handleKeyDown]
  );

  if (!imageUrl) return null;

  return createPortal(
    <Overlay onClick={onClose}>
      <CloseButton onClick={onClose} aria-label="Close">
        ✕
      </CloseButton>
      <Image
        src={imageUrl}
        alt={name || "Dish photo"}
        onClick={(e) => e.stopPropagation()}
      />
      {name && <DishName>{name}</DishName>}
    </Overlay>,
    document.body
  );
};

export default ImageLightbox;
